import { CommonModule, UpperCasePipe } from "@angular/common";
import { Injector, ModuleWithProviders, NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { EffectsModule } from "@ngrx/effects";
import { StoreRouterConnectingModule } from "@ngrx/router-store";
import { ActionReducer, MetaReducer, StoreModule } from "@ngrx/store";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { CowingGuard } from "./guards/cowing.guard";
import { RouterEffects } from "./store/effects/router.effects";
import { UserEffects } from "./store/effects/user.effects";
import { appReducers } from "./store/reducers/app.reducer";
import { storeLogger } from "./store/reducers/logger.reducer";
import { IAppState } from "./store/state/app.state";
import { HttpClient, HttpClientModule } from "@angular/common/http";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { InjectorModule } from "./injector.module";
import { NgImageSliderModule } from "ng-image-slider";

//ANGULAR MATERIAL
import { MatTabsModule } from "@angular/material/tabs";
import { MatTableModule } from "@angular/material/table";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatSortModule } from "@angular/material/sort";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";

// COMPONENTS
import { LoginComponent } from "./components/login/login.component";
import { LandingComponent } from "./components/landing/landing.component";
import { PwdRecoveryComponent } from "./components/pwd-recovery/pwd-recovery.component";
import { CompanyHomeComponent } from "./components/company-home/company-home.component";
import { CoworkingHomeComponent } from "./components/coworking-home/coworking-home.component";
import { CoworkingFunnelComponent } from "./components/coworking-funnel/coworking-funnel.component";
import { CoworkingInfoComponent } from "./components/coworking-info/coworking-info.component";
import { CoworkingStepperComponent } from "./components/coworking-stepper/coworking-stepper.component";
import { CoworkingSpacesComponent } from "./components/coworking-spaces/coworking-spaces.component";
import { CompanySpacesComponent } from "./components/company-spaces/company-spaces.component";
import { CoworkingDetailComponent } from "./components/coworking-detail/coworking-detail.component";
import { CoworkingServicesComponent } from "./components/coworking-services/coworking-services.component";
import { CompanyServicesComponent } from "./components/company-services/company-services.component";
import { CoworkingBankInfoComponent } from "./components/coworking-bank-info/coworking-bank-info.component";
import { CoworkingConfirmComponent } from "./components/coworking-confirm/coworking-confirm.component";
import { CoworkingSpacesHomeComponent } from "./components/coworking-home-spaces/coworking-home-spaces.component";
import { CompanyFunnelComponent } from "./components/company-funnel/company-funnel.component";
import { CompanyPlusFunnelComponent } from './components/company-plus-funnel/company-plus-funnel.component';
import { CompanyInfoComponent } from "./components/company-info/company-info.component";
import { CompanyEmployeesComponent } from "./components/company-employees/company-employees.component";
import { CompanyPaymethodsComponent } from "./components/company-paymethods/company-paymethods.component";
import { CoworkerHomeComponent } from "./components/coworker-home/coworker-home.component";
import { BookingComponent } from "./components/booking/booking.component";
import { CowingCardComponent } from "./components/shared/cowing-card/cowing-card.component";
import { CoworkerBookingComponent } from "./components/coworker-booking/coworker-booking.component";
import { CoworkerConfirmationComponent } from "./components/coworker-confirmation/coworker-confirmation.component";
import { CoworkingBookingsHomeComponent } from "./components/coworking-home-bookings/coworking-home-bookings.component";
import { CompanySpendingsHomeComponent } from "./components/company-home-spendings/company-home-spendings.component";
import { CompanyHomeEmployeesComponent } from "./components/company-home-employees/company-home-employees.component";

// SHARED COMPONENTS
import { CowingAlertWindowComponent } from "./components/shared/cowing-alert-window/cowing-alert-window.component";
import { CowingLoadingWindowComponent } from "./components/shared/cowing-loading-window/cowing-loading-window.component";
import { CowingButtonComponent } from "./components/shared/cowing-button/cowing-button.component";
import { CowingInputComponent } from "./components/shared/cowing-input/cowing-input.component";
import { CowingInputLocationComponent } from "./components/shared/cowing-input-location/cowing-input-location.component";
import { CowingFooterComponent } from "./components/shared/cowing-footer/cowing-footer.component";
import { CowingInputDropdownComponent } from "./components/shared/cowing-input-dropdown/cowing-input-dropdown.component";
import { CowingTimepickerComponent } from "./components/shared/cowing-timepicker/cowing-timepicker.component";
import { CowingInputMediaComponent } from "./components/shared/cowing-input-media/cowing-input-media.component";
import { CowingTextareaComponent } from "./components/shared/cowing-textarea/cowing-textarea.component";
import { CowingCheckboxComponent } from "./components/shared/cowing-checkbox/cowing-checkbox.component";
import { CowingEssentialServiceComponent } from "./components/shared/cowing-essential-service/cowing-essential-service.component";
import { SidebarHomeComponent } from "./components/shared/sidebar-home/sidebar-home.component";
import { HeaderHomeComponent } from "./components/shared/header-home/header-home.component";
import { CowingListElementComponent } from "./components/shared/cowing-list-element/cowing-list-element.component";
import { CowingElementComponent } from "./components/shared/cowing-element/cowing-element.component";
import { CoworkingSpaceCardComponent } from "./components/shared/coworking-space-card/coworking-space-card.component";
import { CowingDatepickerComponent } from "./components/shared/cowing-datepicker/cowing-datepicker.component";
import { CowingSliderComponent } from "./components/shared/cowing-slider/cowing-slider.component";
import { CoworkerBookingCardComponent } from "./components/shared/coworker-booking-card/coworker-booking-card.component";
import { CowingTabsComponent } from "./components/shared/cowing-tabs/cowing-tabs.component";
import { CowingCoworkerCardComponent } from "./components/shared/cowing-coworker-card/cowing-coworker-card.component";
import { CowingTagComponent } from "./components/shared/cowing-tag/cowing-tag.component";
import { CowingBackButtonComponent } from "./components/shared/cowing-back-button/cowing-back-button.component";
import { CowingModalComponent } from "./components/shared/cowing-modal/cowing-modal.component";
import { CompanyEmployeeFormComponent } from "./components/company-employee-form/company-employee-form.component";

// BACKOFFICE
import { BackofficeComponent } from "./components/backoffice/backoffice.component";
import { BackofficeCompaniesComponent } from "./components/backoffice-companies/backoffice-companies.component";
import { BackofficeEditCompanyComponent } from "./components/backoffice-edit-company/backoffice-edit-company.component";
import { BackofficeCoworkersComponent } from "./components/backoffice-coworkers/backoffice-coworkers.component";
import { BackofficeCoworkingsComponent } from "./components/backoffice-coworkings/backoffice-coworkings.component";
import { BackofficeEditCoworkingComponent } from "./components/backoffice-edit-coworking/backoffice-edit-coworking.component";
import { BackofficeUsersComponent } from "./components/backoffice-users/backoffice-users.component";
import { BackofficeSpacesComponent } from "./components/backoffice-spaces/backoffice-spaces.component";
import { BackofficeBookingsComponent } from "./components/backoffice-bookings/backoffice-bookings.component";

// SERVICES
import { CowingCoreService } from "./services/cowingCore.service";
import { CowingSdkService } from "./services/cowingSdk.service";
import { CoworkerService } from "./services/coworker.service";
import { UserService } from "./services/user.service";
import { CustomValidatorsService } from "./services/custom-validators.service";
import { CowingSecurityEncryptService } from "./services/cowingSecurityEncrypt.service";
import { HttpClientService } from "./services/http-client.service";
import { LocalStorageService } from "./services/local-storage.service";

// GUARDS
import { LoggedGuard } from "./guards/cowing.logged-guard";
import { CoworkerProfileComponent } from "./components/coworker-profile/coworker-profile.component";
import { CoworkerInfoComponent } from "./components/coworker-info/coworker-info.component";
import { CoworkerFavComponent } from "./components/coworker-fav/coworker-fav.component";
import { BackofficeEditSpaceComponent } from "./components/backoffice-edit-space/backoffice-edit-space.component";
import { CoworkerOfficeComponent } from './components/coworker-office/coworker-office.component';
import { CompanyPlusInfoComponent } from './components/company-plus-info/company-plus-info.component';
import { CowingDialogComponent } from "./components/shared/cowing-dialog/cowing-dialog.component";

export function logger(reducer: ActionReducer<IAppState>): any {
  // default, no options
  return storeLogger()(reducer);
}

export const metaReducers: MetaReducer<IAppState>[] = [logger];

@NgModule({
  declarations: [
    LoginComponent,
    LandingComponent,
    CompanyHomeComponent,
    CoworkingHomeComponent,
    PwdRecoveryComponent,
    CowingButtonComponent,
    CowingBackButtonComponent,
    CowingInputComponent,
    CowingInputLocationComponent,
    CowingAlertWindowComponent,
    CowingLoadingWindowComponent,
    CowingFooterComponent,
    CowingInputDropdownComponent,
    CowingTimepickerComponent,
    CowingInputMediaComponent,
    CoworkingFunnelComponent,
    CompanyFunnelComponent,
    CompanyPlusFunnelComponent,
    CoworkingInfoComponent,
    CompanyInfoComponent,
    CowingDatepickerComponent,
    CompanyEmployeesComponent,
    CompanyPaymethodsComponent,
    CoworkingStepperComponent,
    CoworkingSpacesComponent,
    CompanySpacesComponent,
    CoworkingDetailComponent,
    CowingTextareaComponent,
    CowingCheckboxComponent,
    CowingElementComponent,
    CoworkingSpaceCardComponent,
    CoworkerBookingCardComponent,
    CowingSliderComponent,
    CoworkingServicesComponent,
    CompanyServicesComponent,
    CowingEssentialServiceComponent,
    SidebarHomeComponent,
    HeaderHomeComponent,
    CoworkingBankInfoComponent,
    CoworkingConfirmComponent,
    CowingListElementComponent,
    CoworkingSpacesHomeComponent,
    CoworkingBookingsHomeComponent,
    CompanySpendingsHomeComponent,
    CompanyHomeEmployeesComponent,
    CowingCardComponent,
    CoworkerHomeComponent,
    CowingTabsComponent,
    CowingCoworkerCardComponent,
    CowingTagComponent,
    BookingComponent,
    CoworkerBookingComponent,
    CoworkerProfileComponent,
    CoworkerInfoComponent,
    CoworkerConfirmationComponent,
    BackofficeComponent,
    BackofficeCompaniesComponent,
    BackofficeCoworkingsComponent,
    BackofficeCoworkersComponent,
    BackofficeUsersComponent,
    BackofficeBookingsComponent,
    BackofficeSpacesComponent,
    BackofficeEditCompanyComponent,
    BackofficeEditSpaceComponent,
    BackofficeEditCoworkingComponent,
    CoworkerFavComponent,
    CoworkerOfficeComponent,
    CowingModalComponent,
    CowingDialogComponent,
    CompanyEmployeeFormComponent,
    CompanyPlusInfoComponent,
  ],
  imports: [
    CommonModule,
    FormsModule,
    MatTabsModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    NgImageSliderModule,
    MatSelectModule,
    StoreModule.forRoot(appReducers, { metaReducers }),
    EffectsModule.forRoot([UserEffects, RouterEffects]),
    StoreRouterConnectingModule.forRoot(),
    TranslateModule.forRoot(),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
    }),
    ReactiveFormsModule,
    HttpClientModule,
  ],
  providers: [
    CowingGuard,
    LoggedGuard,
    CowingSdkService,
    UpperCasePipe,
    UserService,
    CoworkerService,
    CustomValidatorsService,
    LocalStorageService,
    HttpClientModule,
    HttpClientService,
  ],
  exports: [
    LoginComponent,
    LandingComponent,
    CompanyHomeComponent,
    CoworkingHomeComponent,
    CowingAlertWindowComponent,
    CowingLoadingWindowComponent,
    PwdRecoveryComponent,
    CoworkingFunnelComponent,
    CompanyFunnelComponent,
    CompanyPlusFunnelComponent,
    CoworkingBankInfoComponent,
    CoworkingSpacesComponent,
    CompanySpacesComponent,
    CoworkingDetailComponent,
    CoworkingServicesComponent,
    CompanyServicesComponent,
    CoworkingInfoComponent,
    CompanyInfoComponent,
    CompanyEmployeesComponent,
    CompanyPaymethodsComponent,
    CoworkingSpacesHomeComponent,
    CoworkingBookingsHomeComponent,
    CompanySpendingsHomeComponent,
    BookingComponent,
    CoworkerBookingComponent,
    CoworkerConfirmationComponent,
    CoworkerHomeComponent,
    CoworkerProfileComponent,
    CoworkerInfoComponent,
    BackofficeComponent,
    BackofficeCompaniesComponent,
    BackofficeCoworkingsComponent,
    BackofficeCoworkersComponent,
    BackofficeUsersComponent,
    BackofficeBookingsComponent,
    BackofficeSpacesComponent,
    BackofficeEditCompanyComponent,
    BackofficeEditSpaceComponent,
    BackofficeEditCoworkingComponent,
    CoworkerFavComponent,
    CoworkerOfficeComponent,
  ],
})
export class CowingCoreModule {
  public static forRoot(): ModuleWithProviders {
    return {
      ngModule: CowingCoreModule,
      providers: [CowingCoreService, CowingGuard, LoggedGuard],
    };
  }
}

export { CowingCoreService } from "./services/cowingCore.service";
export { CowingSdkService } from "./services/cowingSdk.service";
export { UserService } from "./services/user.service";
export { CustomValidatorsService } from "./services/custom-validators.service";
export { HttpClientService } from "./services/http-client.service";
export { LocalStorageService } from "./services/local-storage.service";
export { CowingGuard } from "./guards/cowing.guard";
export { LoggedGuard } from "./guards/cowing.logged-guard";
